import * as Sentry from '@sentry/react';
import { hydrateSessionFromSeed, purgeSeedForCurrentUser } from './cacheSeed';
import { withDeadline } from '../utils/bootTrace';

/**
 * Prepara la caché de Apollo del arranque SIN poder colgarlo.
 *
 * ── Por qué existe ────────────────────────────────────────────────────────
 *
 * `AppApolloProvider` enseña `<SplashScreen/>` hasta que su `init()` termina,
 * e `init()` esperaba dos cosas sin tope:
 *
 *   1. `hydrateSessionFromSeed()` → lee la semilla de IndexedDB. En un arranque
 *      en frío (la app se cerró, o se quedó sin batería) `sessionStorage` está
 *      vacío y esta lectura SIEMPRE se hace. Con IndexedDB atascada, no
 *      vuelve.
 *   2. `persistor.restore()` → con un snapshot ilegible, RECHAZA; el
 *      `init().catch(console.error)` se lo traga y el provider no sale nunca
 *      del splash.
 *
 * En los dos casos el operario ve «Cargando…» para siempre. Medido en una Tab
 * Active5 el 2026-09-23 con el primer arreglo del arranque ya puesto: React
 * montaba a los 3 s… y se quedaba en el splash a los 5, 10, 20, 30 y 45 s. Era
 * el mismo cuelgue de antes, un piso más abajo, y la primera prueba en tablet
 * no lo vio porque recargaba la página: el snapshot seguía en `sessionStorage`
 * y la lectura de la semilla se saltaba.
 *
 * ── Qué hace ahora ─────────────────────────────────────────────────────────
 *
 * - La semilla con plazo. Es una optimización —arrancar con los datos de la
 *   última vez—, así que perderla es arrancar en frío, que es exactamente lo
 *   que hacía la app antes de que existiera la semilla.
 * - Un snapshot que no se deja restaurar se tira, junto con la semilla que lo
 *   trajo, y se arranca con la caché vacía. Sin tirar la semilla, el siguiente
 *   arranque en frío la volvería a copiar y a fallar: sería permanente.
 *
 * El persistor se crea DESPUÉS de sembrar y por eso se recibe una fábrica: la
 * semilla tiene que estar en `sessionStorage` antes de que `restore()` la lea.
 */

/** Lectura LOCAL de IndexedDB: mismo criterio que los plazos del arranque nativo. */
export const SEED_HYDRATE_DEADLINE_MS = 3_000;

export interface RestorablePersistor {
  restore(): Promise<void>;
  purge(): Promise<void>;
}

export async function restoreApolloCache<P extends RestorablePersistor>(
  createPersistor: () => P
): Promise<P> {
  await withDeadline(
    (isAbandoned) => hydrateSessionFromSeed(isAbandoned),
    SEED_HYDRATE_DEADLINE_MS,
    false,
    'apollo:seed'
  );

  const persistor = createPersistor();
  try {
    await persistor.restore();
  } catch (error) {
    console.warn(
      '[boot] Apollo snapshot could not be restored — starting with an empty cache',
      error
    );
    Sentry.captureException(error, { tags: { feature: 'boot', step: 'apollo:restore' } });
    try {
      await persistor.purge();
    } catch {
      // Si ni siquiera se deja borrar, se sigue igual: la caché en memoria
      // está vacía y la app funciona contra la red.
    }
    // Sin esperar: toca IndexedDB, que puede ser justo lo que está atascado.
    void purgeSeedForCurrentUser();
  }
  return persistor;
}
